// Mobile Playoffs — ONE round per page. The bracket advances a whole round at a time. The shell's
// advance bar drives it in two steps: "Sim {Round}" sims the round (this screen stays put so you
// can review it), then "Continue to {next round}" moves the view forward. The displayed round is
// shared with the shell via the playoffsRound store, so the two stay in lockstep.

import { useLayoutEffect, useRef, useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import {
	setPlayoffsRound,
	usePlayoffsRound,
} from "../util/playoffsRound.ts";
import type { View } from "../../common/types.ts";

// Once the user sims a round, play it back one game-day at a time across ALL series at once
// (like watching the bracket fill in), advancing every series by a game each tick until the round
// is done. The real (instant) worker result is held back until this playback catches up.
const DAY_MS = 600;

const GmPlayoffs = ({
	series,
	confNames,
	numGamesToWinSeries,
	finalMatchups,
	playIns,
	currentRound,
}: View<"playoffs"> & { currentRound?: number }) => {
	useTitleBar({ title: "Playoffs" });
	const { userTid, season } = useLocal(["userTid", "season"]);

	const total = series.length;
	// The play-in tournament is a virtual round -1, shown before Round 1. currentRound is -1 while
	// it's live, so it becomes the default tab then. Its matchups are single games (win-and-in).
	const hasPlayIn = !!playIns && playIns.length > 0;
	const minRound = hasPlayIn ? -1 : 0;
	const roundName = (r: number) => {
		if (r === -1) return "Play-In Tournament";
		if (r === total - 1) return "Finals";
		if (r === total - 2) return "Conference Finals";
		if (r === total - 3) return "Conference Semifinals";
		return `Round ${r + 1}`;
	};
	const roundShort = (r: number) => {
		if (r === -1) return "Play-In";
		if (r === total - 1) return "Finals";
		if (r === total - 2) return "Conf";
		if (r === total - 3) return "Semis";
		return `R${r + 1}`;
	};

	const clamp = (r: number) => Math.max(minRound, Math.min(total - 1, r));

	// The displayed round comes from the shell-shared store so "Continue to Round N" (in the advance
	// bar) moves this screen too. Before the store is initialized for this postseason, fall back to
	// the live round. Manual tab/arrow navigation writes back to the store.
	const poStore = usePlayoffsRound();
	const poKey = `po-${season}`;
	const sel = clamp(
		poStore.key === poKey ? poStore.round : (currentRound ?? 0),
	);
	const setSel = (r: number) => setPlayoffsRound(clamp(r), poKey);

	const hasSeries = total > 0 && series[0] && series[0]!.length > 0;

	// One series row: fixed team tags (seed + abbrev) on the left, a horizontally scrollable games
	// grid in the middle (G1/G2/… with the two teams' stacked scores), win counts on the right. When
	// a round is played back, only the first `showN` games are revealed — so every series fills in
	// one game-day at a time — and the win counts reflect just the revealed games.
	const SeriesCard = ({
		m,
		toWin,
	}: {
		m: { home?: any; away?: any; games?: { home: number; away: number }[] };
		toWin: number;
	}) => {
		const gamesAll = m.games ?? [];
		// Hidden (0) until the round is decided (hides the instant worker result); during playback,
		// animDay games; at rest, all of them.
		const showN = !decided
			? 0
			: animating
				? Math.min(animDay, gamesAll.length)
				: gamesAll.length;
		const games = gamesAll.slice(0, showN);
		const meta = (t: any, homeSide: boolean) => {
			if (!t) return { bye: true } as const;
			const won = games.reduce(
				(s: number, g: any) =>
					s + ((homeSide ? g.home > g.away : g.away > g.home) ? 1 : 0),
				0,
			);
			return {
				bye: false as const,
				seed: t.seed,
				abbrev: t.abbrev ?? "—",
				won,
				winner: won >= toWin,
				isUser: t.tid === userTid,
			};
		};
		const home = meta(m.home, true);
		const away = meta(m.away, false);
		const live =
			animating && showN > 0 && !("winner" in home && home.winner) &&
			!("winner" in away && away.winner);

		const Tag = ({ t }: { t: any }) =>
			t.bye ? (
				<div className="gmp-r gmp-tag gmp-bye">Bye / TBD</div>
			) : (
				<div
					className={`gmp-r gmp-tag ${t.winner ? "win" : ""} ${t.isUser ? "user" : ""}`}
				>
					<span className="gmp-seed">{t.seed}</span>
					<span className="gmp-abbrev">{t.abbrev}</span>
				</div>
			);

		const Won = ({ t }: { t: any }) => (
			<div className={`gmp-r gmp-won ${t.winner ? "win" : ""}`}>
				{t.bye ? "" : t.won}
			</div>
		);
		return (
			<div className={`gmp-series ${live ? "gmp-series-live" : ""}`}>
				<div className="gmp-scol">
					<div className="gmp-hd" />
					<Tag t={home} />
					<Tag t={away} />
				</div>
				<div className="gmp-games">
					{games.map((g, i) => (
						<div key={i} className="gmp-gcol">
							<div className="gmp-hd gmp-glabel">G{i + 1}</div>
							<div className={`gmp-r gmp-gscore ${g.home > g.away ? "gwin" : ""}`}>
								{g.home}
							</div>
							<div className={`gmp-r gmp-gscore ${g.away > g.home ? "gwin" : ""}`}>
								{g.away}
							</div>
						</div>
					))}
				</div>
				<div className="gmp-scol">
					<div className="gmp-hd" />
					<Won t={home} />
					<Won t={away} />
				</div>
			</div>
		);
	};

	// The play-in tab as a bracket, not a flat list. Per conference there are three games with a
	// clear flow: the 7/8 game (winner clinches #7), the 9/10 elimination game (loser is out), and
	// the #8 seed game fed by the 7/8 loser and the 9/10 winner. Each team gets an outcome badge so
	// it's obvious who's IN, who's OUT, and who lives to play the final game.
	const PlayInBracket = () => {
		if (!playIns || playIns.length === 0) {
			return <p className="gmp-empty">The play-in isn't set yet.</p>;
		}

		// Badge text/tone for a team given which game it's in and whether it won.
		const badgeFor = (
			kind: "seed7" | "elim" | "seed8",
			won: boolean,
		): { text: string; tone: "in" | "out" | "alive" } => {
			if (kind === "seed7") {
				return won
					? { text: "Clinched #7", tone: "in" }
					: { text: "To #8 game", tone: "alive" };
			}
			if (kind === "elim") {
				return won
					? { text: "To #8 game", tone: "alive" }
					: { text: "Eliminated", tone: "out" };
			}
			return won
				? { text: "Clinched #8", tone: "in" }
				: { text: "Eliminated", tone: "out" };
		};

		const TeamRow = ({
			m,
			side,
			kind,
			played,
		}: {
			m: any;
			side: "home" | "away";
			kind: "seed7" | "elim" | "seed8";
			played: boolean;
		}) => {
			const t = m[side];
			const won = (t?.won ?? 0) >= 1;
			const g = m.games?.[0];
			const pts = g ? (side === "home" ? g.home : g.away) : undefined;
			const isUser = t?.tid === userTid;
			const badge = played ? badgeFor(kind, won) : null;
			return (
				<div
					className={`gmp-pi-row ${played ? (won ? "win" : "lose") : ""} ${
						isUser ? "user" : ""
					}`}
				>
					<span className="gmp-seed">{t?.seed ?? "—"}</span>
					<span className="gmp-abbrev">{t?.abbrev ?? "—"}</span>
					<span className="gmp-pi-spacer" />
					{pts !== undefined ? <span className="gmp-pi-pts">{pts}</span> : null}
					{badge ? (
						<span className={`gmp-pi-badge ${badge.tone}`}>{badge.text}</span>
					) : null}
				</div>
			);
		};

		const Game = ({
			title,
			stakes,
			m,
			kind,
			feeders,
		}: {
			title: string;
			stakes: string;
			m: any;
			kind: "seed7" | "elim" | "seed8";
			feeders?: [string, string];
		}) => {
			const played =
				!!m && ((m.home?.won ?? 0) + (m.away?.won ?? 0) >= 1);
			return (
				<div className={`gmp-pi-game ${played ? "played" : ""}`}>
					<div className="gmp-pi-cap">
						<span className="gmp-pi-gname">{title}</span>
						<span className="gmp-pi-stakes">{stakes}</span>
					</div>
					<div className="gmp-pi-rows">
						{m ? (
							<>
								<TeamRow m={m} side="home" kind={kind} played={played} />
								<TeamRow m={m} side="away" kind={kind} played={played} />
							</>
						) : (
							<>
								<div className="gmp-pi-row tbd">{feeders?.[0]}</div>
								<div className="gmp-pi-row tbd">{feeders?.[1]}</div>
							</>
						)}
					</div>
				</div>
			);
		};

		return (
			<>
				{(playIns as any[]).map((pi, ci) => {
					const confLabel =
						playIns.length > 1
							? (confNames?.[ci] ?? `Bracket ${ci + 1}`)
							: null;
					return (
						<div className="gmp-pi-conf" key={ci}>
							{confLabel ? (
								<div className="gmp-pi-confname">{confLabel}</div>
							) : null}
							<Game
								title="#7 Seed Game"
								stakes="Winner is in — #7 seed"
								m={pi[0]}
								kind="seed7"
							/>
							<div className="gmp-pi-conn">↓ loser drops to the #8 seed game</div>
							<Game
								title="Elimination Game"
								stakes="Loser is out"
								m={pi[1]}
								kind="elim"
							/>
							<div className="gmp-pi-conn">↓ winner advances to the #8 seed game</div>
							<Game
								title="#8 Seed Game"
								stakes="Winner grabs the final playoff spot"
								m={pi[2]}
								kind="seed8"
								feeders={["Loser of #7 seed game", "Winner of elimination game"]}
							/>
						</div>
					);
				})}
			</>
		);
	};

	const isPlayIn = sel === -1;
	// For the animation/round bookkeeping below we still need a flat view of the play-in games, but
	// the play-in tab renders its own bracket (PlayInBracket) instead of a flat SeriesCard list — a
	// flat list hides that the 7/8 winner is already IN while the 9/10 loser is already OUT.
	const round: any[] = isPlayIn
		? playIns
			? (playIns as any[]).flat()
			: []
		: hasSeries
			? (series[sel] ?? [])
			: [];
	const toWin = isPlayIn ? 1 : (numGamesToWinSeries[sel] ?? 4);

	// A round is "decided" once every series has a winner. We only play back a round once (guarded
	// by a per-result signature), the first time we see its final result — revisiting a resolved
	// round shows finals with no animation.
	const decided =
		round.length > 0 &&
		round.every(
			(m) => (m.home?.won ?? 0) >= toWin || (m.away?.won ?? 0) >= toWin,
		);
	const resultSig =
		`${poKey}:${sel}:` +
		round.map((m) => `${m.home?.won ?? 0}-${m.away?.won ?? 0}`).join(",");
	// Games in the longest series = number of "days" to play back.
	const maxGames = decided
		? Math.max(1, ...round.map((m) => (m.home?.won ?? 0) + (m.away?.won ?? 0)))
		: 0;

	const animatedRef = useRef<Set<string>>(new Set());
	// animDay = how many game-days have been played back so far. Infinity = show finals (resting).
	const [animDay, setAnimDay] = useState(Infinity);

	// Seed the round that was already resolved on first mount so a fresh load doesn't replay it;
	// only rounds that transition to decided while watching (i.e. the one you just simmed) animate.
	const seededRef = useRef(false);
	if (!seededRef.current) {
		seededRef.current = true;
		if (decided) {
			animatedRef.current.add(resultSig);
		}
	}
	// useLayoutEffect (not useEffect) so we set animDay=0 before the browser paints the finals,
	// avoiding the "flash the result, then clear and re-animate" glitch.
	useLayoutEffect(() => {
		if (decided && !animatedRef.current.has(resultSig)) {
			animatedRef.current.add(resultSig);
			setAnimDay(0);
			let d = 0;
			const id = setInterval(() => {
				d += 1;
				setAnimDay(d);
				if (d >= maxGames) {
					clearInterval(id);
					setAnimDay(Infinity);
				}
			}, DAY_MS);
			return () => clearInterval(id);
		}
		// Not decided (pre-sim / mid-sim) or an already-seen round: no active playback.
		setAnimDay(Infinity);
	}, [resultSig, decided, maxGames]);
	const animating = decided && animDay !== Infinity;

	return (
		<div className="gmp">
			<style>{styles}</style>
			<h1 className="gmp-title">Playoffs</h1>
			{!finalMatchups ? (
				<p className="gmp-proj">Projected matchups if the season ended today.</p>
			) : null}

			{!hasSeries ? (
				<p className="gmp-empty">The bracket isn't set yet.</p>
			) : (
				<>
					<div className="gmp-tabs">
						{hasPlayIn ? (
							<button
								key="playin"
								className={`gmp-tab ${sel === -1 ? "active" : ""} ${
									currentRound === -1 ? "live" : ""
								}`}
								onClick={() => setSel(-1)}
							>
								Play-In
							</button>
						) : null}
						{series.map((_: any, r: number) => (
							<button
								key={r}
								className={`gmp-tab ${r === sel ? "active" : ""} ${
									r === currentRound ? "live" : ""
								}`}
								onClick={() => setSel(r)}
							>
								{roundShort(r)}
							</button>
						))}
					</div>

					<div className="gmp-nav">
						<button
							className="gmp-arrow"
							disabled={sel === minRound}
							onClick={() => setSel(clamp(sel - 1))}
							aria-label="Previous round"
						>
							‹
						</button>
						<div className="gmp-navmid">
							<div className="gmp-navname">{roundName(sel)}</div>
							<div className="gmp-navsub">
								{isPlayIn ? "Winners advance to Round 1" : `Round ${sel + 1} of ${total}`}
								{sel === currentRound ? " · live" : ""}
							</div>
						</div>
						<button
							className="gmp-arrow"
							disabled={sel === total - 1}
							onClick={() => setSel(clamp(sel + 1))}
							aria-label="Next round"
						>
							›
						</button>
					</div>

					{isPlayIn ? (
						<PlayInBracket />
					) : round.length === 0 ? (
						<p className="gmp-empty">This round isn't set yet.</p>
					) : (
						round.map((m: any, i: number) => (
							<SeriesCard key={i} m={m} toWin={toWin} />
						))
					)}
				</>
			)}

			{confNames && confNames.length ? (
				<div className="gmp-confs">{confNames.filter(Boolean).join(" · ")}</div>
			) : null}
		</div>
	);
};

const styles = `
.gmp { max-width:640px; margin:0 auto; padding:16px 16px 40px; color:#fff; font-family:Montserrat,system-ui,sans-serif; }
.gmp-title { font-weight:900; font-size:30px; margin:0 0 10px; letter-spacing:-.5px; }
.gmp-proj { color:#8fb3c9; font-size:12px; margin:0 0 14px; }
.gmp-empty { color:#8a8a8a; padding:20px 0; text-align:center; }
.gmp-tabs { display:flex; gap:6px; background:#151515; border:1px solid #232323; border-radius:12px;
  padding:5px; margin-bottom:14px; }
.gmp-tab { flex:1; border:none; background:none; color:#8a8a8a; font-family:inherit; font-weight:800;
  font-size:12px; padding:8px 0; border-radius:8px; cursor:pointer; position:relative; }
.gmp-tab.active { background:#232323; color:#fff; }
.gmp-tab.live { color:#18E0A8; }
.gmp-tab.active.live { background:rgba(24,224,168,.14); color:#18E0A8; }
.gmp-nav { display:flex; align-items:center; gap:10px; margin-bottom:14px; }
.gmp-arrow { width:40px; height:40px; border-radius:50%; border:1px solid #2a2a2a; background:#181818;
  color:#cdd4da; font-size:22px; line-height:1; cursor:pointer; flex:none; font-family:inherit; }
.gmp-arrow:disabled { opacity:.3; cursor:default; }
.gmp-navmid { flex:1; text-align:center; }
.gmp-navname { font-weight:900; font-size:20px; letter-spacing:-.5px; }
.gmp-navsub { font-size:11px; color:#8fb3c9; font-weight:600; margin-top:1px; }
.gmp-series { display:flex; align-items:stretch; gap:12px; background:#181818; border:1px solid #232323;
  border-radius:12px; padding:8px 12px 10px; margin-bottom:9px; transition:border-color .2s ease; }
/* Highlighted while a series is still being played back (day-by-day reveal). */
.gmp-series-live { border-color:#2f4a44; }
/* Fixed left (team tags) + right (win counts) columns; each stacks a header spacer then the two
   team rows, so their heights line up with the scrollable games grid in the middle. */
.gmp-scol { flex:0 0 auto; display:flex; flex-direction:column; }
.gmp-games { flex:1 1 auto; min-width:0; display:flex; gap:16px; overflow-x:auto; scrollbar-width:none; }
.gmp-games::-webkit-scrollbar { display:none; }
.gmp-gcol { flex:0 0 auto; display:flex; flex-direction:column; align-items:center; }
/* Shared row heights so the G labels and both teams' rows align across all three sections. */
.gmp-hd { height:15px; display:flex; align-items:center; justify-content:center; }
.gmp-r { height:30px; display:flex; align-items:center; }
.gmp-glabel { font-size:9px; font-weight:800; color:#6b7681; letter-spacing:.4px; }
.gmp-tag { gap:9px; }
.gmp-seed { width:22px; height:22px; border-radius:6px; background:#232323; color:#9fb0bc; font-size:11px;
  font-weight:800; display:flex; align-items:center; justify-content:center; flex:0 0 auto; }
.gmp-abbrev { font-weight:800; font-size:14px; letter-spacing:.2px; }
.gmp-gscore { justify-content:center; font-size:14px; font-weight:800; color:#7f8b93; min-width:30px; }
.gmp-gscore.gwin { color:#e6e6e6; }
.gmp-won { justify-content:flex-end; min-width:16px; font-weight:900; font-size:18px; }
.gmp-tag.win .gmp-abbrev, .gmp-won.win { color:#18E0A8; }
.gmp-tag.win .gmp-seed { background:rgba(24,224,168,.15); color:#18E0A8; }
.gmp-tag.user .gmp-abbrev { text-decoration:underline; text-decoration-color:#4A9EFF; text-underline-offset:3px; }
.gmp-bye { color:#55606a; font-size:13px; }
.gmp-confs { text-align:center; color:#55606a; font-size:11px; margin-top:20px; }

/* ---- Play-In bracket ---- */
.gmp-pi-conf { margin-bottom:8px; }
.gmp-pi-conf + .gmp-pi-conf { margin-top:22px; padding-top:22px; border-top:1px solid #232323; }
.gmp-pi-confname { font-weight:900; font-size:13px; letter-spacing:.4px; text-transform:uppercase;
  color:#8fb3c9; margin-bottom:10px; }
.gmp-pi-game { background:#181818; border:1px solid #232323; border-radius:12px; padding:9px 12px 10px; }
.gmp-pi-game.played { border-color:#2a2a2a; }
.gmp-pi-cap { display:flex; align-items:baseline; justify-content:space-between; gap:8px;
  margin-bottom:6px; }
.gmp-pi-gname { font-weight:900; font-size:13px; letter-spacing:-.2px; color:#e6e6e6; }
.gmp-pi-stakes { font-size:10px; font-weight:700; color:#8fb3c9; text-align:right; }
.gmp-pi-rows { display:flex; flex-direction:column; gap:2px; }
.gmp-pi-row { display:flex; align-items:center; gap:9px; height:32px; }
.gmp-pi-row.tbd { color:#55606a; font-size:12px; font-weight:600; font-style:italic; }
.gmp-pi-spacer { flex:1 1 auto; }
.gmp-pi-pts { font-weight:800; font-size:15px; color:#7f8b93; min-width:34px; text-align:right; }
.gmp-pi-row.win .gmp-pi-pts { color:#e6e6e6; }
.gmp-pi-row.win .gmp-abbrev { color:#18E0A8; }
.gmp-pi-row.win .gmp-seed { background:rgba(24,224,168,.15); color:#18E0A8; }
.gmp-pi-row.lose .gmp-abbrev, .gmp-pi-row.lose .gmp-seed { opacity:.55; }
.gmp-pi-row.user .gmp-abbrev { text-decoration:underline; text-decoration-color:#4A9EFF;
  text-underline-offset:3px; }
.gmp-pi-badge { font-size:9px; font-weight:900; letter-spacing:.3px; text-transform:uppercase;
  padding:3px 7px; border-radius:6px; white-space:nowrap; }
.gmp-pi-badge.in { background:rgba(24,224,168,.15); color:#18E0A8; }
.gmp-pi-badge.out { background:rgba(255,90,90,.13); color:#ff7a7a; }
.gmp-pi-badge.alive { background:rgba(74,158,255,.13); color:#7db4ff; }
.gmp-pi-conn { display:flex; align-items:center; gap:8px; font-size:10px; font-weight:700;
  color:#6b7681; padding:5px 12px; }
`;

export default GmPlayoffs;
